import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BAD_CHECKSUM = 0x15
const BLOCK_SIZE = 256
const MEMORY_SIZE = 0x8000
const CHANNEL_SIZE = 48
const CHANNEL_START = 0x2000
const SKIP_START = 0x0070
const CHANNEL_FLAGS = 32
const POWER_MASK = 0x20
const WIDE_MASK = 0x80
const DCS_MASK = 0x2800
const DCS_INVERSE = 0x8000

type Kenwood60GSpec = {
  id: string
  model: string
  type: string
  summary: string
  channels: 8 | 32 | 128
  capacities: Readonly<Record<string, number>>
}

export type Kenwood60GProfile = {
  protocol: 'kenwood60g'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  parity: 'even'
  channelMaximum: number
  nameLength: 8
  memorySize: number
  powerLevels: readonly ['Low', 'High']
  types: readonly string[]
  capacities: Readonly<Record<string, number>>
}

const SPECS: readonly Kenwood60GSpec[] = [
  {
    id: 'kenwood-tk-260g',
    model: 'TK-260G',
    type: 'P2600',
    summary: 'Kenwood ProTalk TK-260G VHF portable.',
    channels: 8,
    capacities: { u: 8, t: 8, $: 8, '\u0014': 8, '\u0005': 8, '\u0004': 8 },
  },
  {
    id: 'kenwood-tk-270g',
    model: 'TK-270G',
    type: 'P2700',
    summary: 'Kenwood ProTalk TK-270G VHF portable.',
    channels: 128,
    capacities: { t: 128, $: 128, '\u0014': 128, '\u0005': 128, '\u0004': 128 },
  },
  {
    id: 'kenwood-tk-272g',
    model: 'TK-272G',
    type: 'P2720',
    summary: 'Kenwood ProTalk TK-272G VHF portable.',
    channels: 32,
    capacities: { '\u0005': 32, '\u0004': 32 },
  },
  {
    id: 'kenwood-tk-278g',
    model: 'TK-278G',
    type: 'P2780',
    summary: 'Kenwood ProTalk TK-278G VHF portable.',
    channels: 128,
    capacities: { '\u0005': 128, '\u0004': 128, '\u0015': 16, '\u0014': 16 },
  },
  {
    id: 'kenwood-tk-360g',
    model: 'TK-360G',
    type: 'P3600',
    summary: 'Kenwood ProTalk TK-360G UHF portable.',
    channels: 8,
    capacities: {
      '\u0006': 8,
      '\u0007': 8,
      '\u0008': 8,
      '\u0009': 8,
      '&': 8,
      ')': 8,
      '\u0016': 8,
      '\u0017': 8,
      '\u0019': 8,
      v: 8,
      y: 8,
    },
  },
  {
    id: 'kenwood-tk-370g',
    model: 'TK-370G',
    type: 'P3700',
    summary: 'Kenwood ProTalk TK-370G UHF portable.',
    channels: 128,
    capacities: {
      '\u0006': 128,
      '\u0007': 128,
      '\u0008': 128,
      '\u0009': 128,
      '\u0016': 128,
      '\u0017': 128,
      '\u0018': 128,
      '\u0019': 128,
      '&': 128,
      v: 128,
    },
  },
  {
    id: 'kenwood-tk-372g',
    model: 'TK-372G',
    type: 'P3720',
    summary: 'Kenwood ProTalk TK-372G UHF portable.',
    channels: 32,
    capacities: { '\u0006': 32, '\u0007': 32, '\u0008': 32, '\u0009': 32 },
  },
  {
    id: 'kenwood-tk-378g',
    model: 'TK-378G',
    type: 'P3780',
    summary: 'Kenwood ProTalk TK-378G UHF portable.',
    channels: 128,
    capacities: { '\u0016': 16, '\u0017': 16, '6': 128, '9': 128 },
  },
  {
    id: 'kenwood-tk-388g',
    model: 'TK-388G',
    type: 'P3880',
    summary: 'Kenwood ProTalk TK-388G UHF portable.',
    channels: 128,
    capacities: { '\u001b': 128 },
  },
  {
    id: 'kenwood-tk-760g',
    model: 'TK-760G',
    type: 'M7600',
    summary: 'Kenwood TK-760G VHF mobile.',
    channels: 128,
    capacities: { '\u0005': 128, '\u0004': 128, '\u0014': 128, t: 128 },
  },
  {
    id: 'kenwood-tk-762g',
    model: 'TK-762G',
    type: 'M7620',
    summary: 'Kenwood TK-762G VHF mobile.',
    channels: 8,
    capacities: { '\u0005': 8, '\u0004': 8, $: 8, t: 8 },
  },
  {
    id: 'kenwood-tk-768g',
    model: 'TK-768G',
    type: 'M7680',
    summary: 'Kenwood TK-768G VHF mobile.',
    channels: 128,
    capacities: { '\u0015': 8, '\u0014': 8, '5': 128, '4': 128 },
  },
  {
    id: 'kenwood-tk-860g',
    model: 'TK-860G',
    type: 'M8600',
    summary: 'Kenwood TK-860G UHF mobile.',
    channels: 128,
    capacities: {
      '\u0008': 128,
      '\u0006': 128,
      '\u0007': 128,
      '\u0018': 128,
      '\u0016': 128,
      '\u0017': 128,
    },
  },
  {
    id: 'kenwood-tk-862g',
    model: 'TK-862G',
    type: 'M8620',
    summary: 'Kenwood TK-862G UHF mobile.',
    channels: 8,
    capacities: { '\u0006': 8, '\u0007': 8, '&': 8, v: 8 },
  },
  {
    id: 'kenwood-tk-868g',
    model: 'TK-868G',
    type: 'M8680',
    summary: 'Kenwood TK-868G UHF mobile.',
    channels: 128,
    capacities: { '\u0018': 8, ';': 128, '8': 128, '6': 128 },
  },
]

export const KENWOOD_60G_PROFILES: readonly Kenwood60GProfile[] = SPECS.map(
  (spec) => ({
    protocol: 'kenwood60g',
    id: spec.id,
    vendor: 'Kenwood',
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      'Variant-aware channels',
      '8-character names',
    ],
    baudRate: 9600,
    parity: 'even',
    channelMaximum: spec.channels,
    nameLength: 8,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
    types: [spec.type],
    capacities: spec.capacities,
  }),
)

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

function variantKey(image: Uint8Array): string {
  const value = image[0xac]
  return value >= 0x20 && value <= 0x7e
    ? String.fromCharCode(value).toLowerCase()
    : String.fromCharCode(value)
}

export function kenwood60GChannelCount(
  image: Uint8Array,
  profile: Kenwood60GProfile,
): number {
  return profile.capacities[variantKey(image)] ?? profile.channelMaximum
}

function channelOffset(number: number): number {
  return CHANNEL_START + (number - 1) * CHANNEL_SIZE
}

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0xffff || value === 0) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= DCS_MASK) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt((value & 0x07ff).toString(8), 10),
      polarity: value & DCS_INVERSE ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    value =
      DCS_MASK |
      Number.parseInt(channel.dtcs.toString(), 8) |
      (channel.dtcsPolarity === 'R' ? DCS_INVERSE : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function displayName(raw: Uint8Array): string {
  let end = raw.indexOf(0xff)
  if (end < 0) end = raw.indexOf(0)
  if (end < 0) end = raw.length
  return String.fromCharCode(...raw.slice(0, end)).trim()
}

export function parseKenwood60GChannel(
  image: Uint8Array,
  number: number,
): WebChannel {
  const offset = channelOffset(number)
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[12] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(12, 16))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency =
    raw[16] === 0xff ? null : decodeLbcd(raw.slice(16, 20))
  const txTone = decodeTone(raw.slice(24, 26))
  const rxtone = decodeTone(raw.slice(22, 24))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const skipByte = image[SKIP_START + ((number - 1) >> 3)] ?? 0
  return {
    number,
    empty: false,
    name: displayName(raw.slice(2, 10)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[CHANNEL_FLAGS] & WIDE_MASK ? 'FM' : 'NFM',
    power: raw[CHANNEL_FLAGS] & POWER_MASK ? 'High' : 'Low',
    skip: (skipByte & (1 << ((number - 1) & 7))) === 0,
  }
}

export function parseKenwood60GChannels(
  image: Uint8Array,
  profile: Kenwood60GProfile,
): WebChannel[] {
  return Array.from(
    { length: kenwood60GChannelCount(image, profile) },
    (_, index) => parseKenwood60GChannel(image, index + 1),
  )
}

export function applyKenwood60GChannel(
  image: Uint8Array,
  profile: Kenwood60GProfile,
  channel: WebChannel,
): void {
  if (channel.number > kenwood60GChannelCount(image, profile)) {
    throw new Error(
      `This ${profile.model} image has only ${kenwood60GChannelCount(image, profile)} channels.`,
    )
  }
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[12] === 0xff
  if (wasEmpty) {
    raw.fill(0)
    raw[0] = channel.number - 1
    raw[1] = 1
    raw[20] = 0x35
    raw[21] = 0x32
    raw[31] = 0xff
    raw[33] = 0xff
    raw.fill(0xff, 34)
  }
  const name = channel.name
    .toUpperCase()
    .replace(/[^A-Z0-9 _\-*()/\-+=)]/g, '')
    .slice(0, 8)
    .padEnd(8, ' ')
  for (let index = 0; index < 8; index += 1) {
    raw[2 + index] = name.charCodeAt(index)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(12, 16))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(16, 20))
  } else {
    raw.subarray(16, 20).fill(0xff)
  }
  const currentTx = raw.slice(24, 26)
  const currentRx = raw.slice(22, 24)
  if (
    !toneMatches(channel, decodeTone(currentTx)) ||
    (!toneMatches(channel, decodeTone(currentRx)) &&
      decodeTone(currentRx).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 22)
    raw.set(tone, 24)
  }
  const preserve = raw[CHANNEL_FLAGS] & ~(POWER_MASK | WIDE_MASK)
  raw[CHANNEL_FLAGS] =
    preserve |
    (channel.power === 'High' ? POWER_MASK : 0) |
    (channel.mode === 'FM' ? WIDE_MASK : 0)
  const skipOffset = SKIP_START + ((channel.number - 1) >> 3)
  const bit = 1 << ((channel.number - 1) & 7)
  image[skipOffset] = channel.skip
    ? image[skipOffset] & ~bit
    : image[skipOffset] | bit
}

function fill(target: Uint8Array, offset: number, value: Uint8Array): void {
  target.fill(0xff, offset, offset + value.byteLength)
  target.set(value, offset)
}

export function prepareKenwood60GImage(image: Uint8Array): void {
  const banks = new Map<number, number[]>()
  let channelCount = 0
  for (let channel = 0; channel < 128; channel += 1) {
    const offset = channelOffset(channel + 1)
    const bank = image[offset + 1]
    const bankIndex = image[offset]
    if (bankIndex === 0xff || bank === 0xff || bank === 0) continue
    const members = banks.get(bank) ?? []
    members.push(channel)
    banks.set(bank, members)
    channelCount += 1
  }
  image[0x0e] = banks.size
  image[0x0f] = channelCount
  const map: number[] = []
  for (const [bank, channels] of banks) {
    channels.sort((left, right) => left - right)
    channels.forEach((channel, index) => {
      map.push(bank, index + 1, bank - 1, channel)
    })
  }
  fill(image, 0x0300, Uint8Array.from(map))

  const bankList: number[] = []
  for (const [bank, channels] of banks) {
    bankList.push(bank, channels.length, ...Array(14).fill(0xff))
  }
  fill(image, 0x1000, Uint8Array.from(bankList))

  const dtmf: number[] = []
  for (let index = 0; index < channelCount; index += 1) {
    dtmf.push(0x00, 0xf0, 0xff, 0xff, 0xff)
  }
  fill(image, 0x7000, Uint8Array.from(dtmf))
  image.set(new TextEncoder().encode('Chirp'), 0x00bb)
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error(message)
  }
}

async function handshake(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, message)
}

function addressFrame(command: string, address: number): Uint8Array {
  return Uint8Array.of(
    command.charCodeAt(0),
    (address >> 8) & 0xff,
    address & 0xff,
  )
}

async function enterProgrammingMode(
  profile: Kenwood60GProfile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  await readAck(transport, 'The radio refused Kenwood programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  const reportedModel = new TextDecoder('latin1')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
  if (!profile.types.some((type) => reportedModel.toUpperCase().includes(type))) {
    throw new Error(
      `The connected radio identifies as ${reportedModel || 'an unknown model'}, not ${profile.model}.`,
    )
  }
  await handshake(transport, 'The radio rejected the Kenwood session.')
  return reportedModel || profile.model
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.discardInput()
  await transport.write(addressFrame('R', address))
  const marker = (await transport.readExact(1, 4000))[0]
  if (marker === 0x5a) {
    await transport.readExact(1, 2000)
    await handshake(
      transport,
      `The radio rejected empty read block 0x${address.toString(16)}.`,
    )
    return new Uint8Array(BLOCK_SIZE).fill(0xff)
  }
  if (marker !== 0x57) {
    throw new Error(`Unexpected Kenwood read reply at block 0x${address.toString(16)}.`)
  }
  const payload = await transport.readExact(BLOCK_SIZE + 1, 5000)
  const data = payload.slice(0, BLOCK_SIZE)
  if (checksum(data) !== payload[BLOCK_SIZE]) {
    throw new Error(`Block checksum failed at 0x${address.toString(16)}.`)
  }
  await handshake(
    transport,
    `The radio rejected read block 0x${address.toString(16)}.`,
  )
  return data
}

function shouldWriteBlock(
  profile: Kenwood60GProfile,
  address: number,
  data: Uint8Array,
): boolean {
  if (address >= 0x10 && address < 0x1f) {
    return data.some((value) => value !== 0xff)
  }
  if (
    address >= (profile.model === 'TK-260G' ? 0x52 : 0x59) &&
    address < 0x5f
  ) {
    return false
  }
  return true
}

async function writeBlock(
  profile: Kenwood60GProfile,
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<boolean> {
  if (!shouldWriteBlock(profile, address, data)) return false
  const empty = data.every((value) => value === 0xff)
  const frame = new Uint8Array(empty ? 4 : 4 + BLOCK_SIZE)
  frame.set(addressFrame(empty ? 'Z' : 'W', address))
  if (empty) {
    frame[3] = 0xff
  } else {
    frame.set(data, 3)
    frame[3 + BLOCK_SIZE] = checksum(data)
  }
  await transport.write(frame)
  const ack = (await transport.readExact(1, 5000))[0]
  if (ack === BAD_CHECKSUM) {
    throw new Error(`The radio reported a bad checksum writing block 0x${address.toString(16)}.`)
  }
  if (ack !== ACK) {
    throw new Error(`The radio rejected write block 0x${address.toString(16)}.`)
  }
  return true
}

export async function readKenwood60GRadioWithTransport(
  profile: Kenwood60GProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let address = 0; address < total; address += 1) {
      image.set(await readBlock(transport, address), address * BLOCK_SIZE)
      onProgress?.({
        current: address + 1,
        total,
        label: 'Reading Kenwood 60G memory',
      })
    }
    return { image, reportedModel }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeKenwood60GRadioWithTransport(
  profile: Kenwood60GProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than Kenwood 60G memory.')
  }
  prepareKenwood60GImage(image)
  await enterProgrammingMode(profile, transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let address = 0; address < total; address += 1) {
      await writeBlock(
        profile,
        transport,
        address,
        image.slice(address * BLOCK_SIZE, (address + 1) * BLOCK_SIZE),
      )
      onProgress?.({
        current: address + 1,
        total,
        label: 'Writing Kenwood 60G memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export function compareKenwood60GImages(
  left: Uint8Array,
  right: Uint8Array,
): number | null {
  return compareImages(left, right)
}
